import { useState, ReactNode, SyntheticEvent, useRef, useEffect, useCallback } from 'react';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/material';

import { TTabsSize, TTabRowVariant, Maybe } from '@/types';
import { useAppContext } from '@/context';
import { DESKTOP_HEADER_ID, DESKTOP_HEADER_ID_WITH_NAV_MENU, MOBILE_HEADER_ID } from '@/constants';
import { getMinHeaderHeightStyles } from '@/components/Header/Header.styles';

import {
	tabsContentStyles,
	getTabsListWrapStyles,
	getTabsWrapperStyles,
	tabsStyles,
	getTabStyles,
} from './TeamTAGTabs.styles';

const a11yProps = (value: string) => {
	return {
		id: `tab-${value}`,
		'aria-controls': `tabpanel-${value}`,
	};
};

interface TAGTabsProps {
	tabs: {
		label: string;
		value: string;
		href?: string;
		jumplinkName?: string;
		content: ReactNode;
	}[];
	role?: 'default' | 'navigation';
	centered?: boolean;
	dataTestId?: string;
	tabRowVariant?: TTabRowVariant;
	tabsSize?: TTabsSize;
	jumplinkTabs?: Maybe<boolean>;
}

export default function TeamTAGTabs({
	tabs,
	role = 'default',
	centered,
	dataTestId = '',
	tabRowVariant = 'Standard',
	tabsSize = 'Small',
	jumplinkTabs = false,
}: Readonly<TAGTabsProps>) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const isBelowMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
	const [currentValue, setCurrentValue] = useState<null | string>(jumplinkTabs ? null : tabs[0].value);
	const [isTabsFixed, setIsTabsFixed] = useState(false);
	const [activeColor, setActiveColor] = useState('#2288C9'); // Default color for the active tab
	const [_, setForceRender] = useState(false);
	const [isTabsVisible, setIsTabsVisible] = useState(true); // New state for visibility
	const tabsWrapNode = useRef<HTMLDialogElement>(null);

	const { config } = useAppContext();
	const showMobileHeaderLocationBar = config?.featureFlags?.showMobileHeaderLocationBar;
	const showHeaderWithLocationNav = config.featureFlags?.showHeaderWithLocationNav;
	const showHeaderNavigationMenu = config.featureFlags?.showHeaderNavigationMenu;
	const withDisappearingHeader = config.features?.header?.hideHeaderOnScroll;

	const desktopHeaderElement = globalThis?.document?.getElementById(
		withDisappearingHeader ? DESKTOP_HEADER_ID_WITH_NAV_MENU : DESKTOP_HEADER_ID
	);
	const mobileHeaderElement = globalThis?.document?.getElementById(MOBILE_HEADER_ID);

	const desktopHeaderDisappearingTopStyle = desktopHeaderElement?.style?.top;
	const mobileHeaderDisappearingTopStyle = mobileHeaderElement?.style?.top;

	const handleChange = (_: SyntheticEvent, newValue: string) => {
		setCurrentValue(newValue);
	};

	const handleJumplink = (jumplinkName: string) => {
		if (jumplinkName && window) {
			window.location.hash = jumplinkName;
		}
	};

	const sectionColorsRef = useRef<Record<string, string>>({
		'aspen-dental': '#2288C9',
		chapter: '#CE0059',
		clearchoice: '#F99D25',
		wellnow: '#CF2A2A',
		lovet: '#39863C',
	});

	const tabValuesRef = useRef(['aspen-dental', 'chapter', 'clearchoice', 'wellnow', 'lovet']);

	const handleScroll = useCallback(() => {
		const sectionColors = sectionColorsRef.current;
		const tabValues = tabValuesRef.current;
		if (tabsWrapNode.current) {
			const blockOffsetTop = tabsWrapNode.current.offsetTop;
			if (window.scrollY >= blockOffsetTop) {
				setIsTabsFixed(true);

				const observer = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							if (entry.isIntersecting) {
								const sectionId = entry.target.id;
								// Update the state with the new color
								setCurrentValue(tabs[tabValues.findIndex((el) => el === sectionId)].value);
								setActiveColor(sectionColors[sectionId]);
							}
						});
					},
					{ rootMargin: '0px 0px -50% 0px', threshold: 0.1 }
				);

				const sections = document.querySelectorAll('#aspen-dental, #chapter, #clearchoice, #wellnow, #lovet');
				sections.forEach((section) => observer.observe(section));

				setForceRender((prev) => !prev);
				return () => sections.forEach((section) => observer.unobserve(section));
			} else {
				setIsTabsFixed(false);
			}
		}
	}, [sectionColorsRef, tabValuesRef, tabs]);

	useEffect(() => {
		if (role !== 'navigation') return;
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll, role]);

	const headerPositioning = getMinHeaderHeightStyles(
		showMobileHeaderLocationBar,
		showHeaderNavigationMenu,
		showHeaderWithLocationNav
	);

	useEffect(() => {
		// Early return if tabs are not fixed
		if (!isTabsFixed) {
			setIsTabsVisible(true);
			return;
		}

		let hideTimeout: ReturnType<typeof setTimeout> | null = null;

		const handleUserActivity = () => {
			// Show tabs immediately on any user activity
			setIsTabsVisible(true);

			// Clear existing timeout
			if (hideTimeout) {
				clearTimeout(hideTimeout);
			}

			// Set new timeout to hide tabs after 2 seconds of inactivity
			hideTimeout = setTimeout(() => {
				setIsTabsVisible(false);
			}, 2000);
		};

		// Add event listeners for user activity
		const events = ['mousemove', 'scroll'] as const;
		events.forEach((event) => {
			window.addEventListener(event, handleUserActivity, { passive: true });
		});

		// Initial timeout when tabs become fixed
		hideTimeout = setTimeout(() => {
			setIsTabsVisible(false);
		}, 2000);

		// Cleanup function
		return () => {
			events.forEach((event) => {
				window.removeEventListener(event, handleUserActivity);
			});

			if (hideTimeout) {
				clearTimeout(hideTimeout);
			}
		};
	}, [isTabsFixed]); // Removed unnecessary dependencies

	return (
		<Box
			sx={tabsContentStyles({ isTabsFixed })}
			style={{
				visibility: isTabsFixed && !isTabsVisible ? 'hidden' : 'visible',
				transition: 'visibility 0.3s ease',
			}}
			ref={tabsWrapNode}
			data-testid={'tag_tabs'}
			id="tag_tabs"
		>
			<Box sx={getTabsListWrapStyles({ tabRowVariant, isSmallScreen })}>
				<Box
					sx={getTabsWrapperStyles({
						tabRowVariant,
						isTabsFixed,
						headerPositioning,
						withDisappearingHeader,
						desktopHeaderDisappearingTopStyle,
						mobileHeaderDisappearingTopStyle,
					})}
				>
					<Tabs
						value={currentValue || false}
						onChange={handleChange}
						centered={centered}
						orientation="vertical"
						variant={isBelowMediumScreen ? 'scrollable' : 'fullWidth'}
						sx={tabsStyles}
						scrollButtons={false}
					>
						{tabs.map(({ label, href, value, jumplinkName }) => (
							<Tab
								key={value}
								sx={getTabStyles({ tabsSize, tabRowVariant, isSmallScreen })}
								{...a11yProps(value)}
								{...(href ? { href } : {})}
								value={value}
								label={label}
								data-testid={`${dataTestId}_${value}`}
								onClick={() => jumplinkName && handleJumplink(jumplinkName)}
							/>
						))}
					</Tabs>
					<Box
						sx={{
							width: '4px', // Desired thickness of the line
							bgcolor: activeColor, // Desired color of the line
							transition: 'background-color 0.5s ease',
						}}
					/>
				</Box>
			</Box>
		</Box>
	);
}
